import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft, Pencil } from 'lucide-react'
import { MessageList } from './MessageList'
import { MessageInput } from './MessageInput'
import { TaskBadge } from './TaskBadge'
import { isWorkflowInProgress as checkWorkflowInProgress, isWorkflowBlocking as checkWorkflowBlocking } from '@/lib/workflow-utils'
import type { ActiveWorkflow } from '@/types/conversations'
import { NextStepsPanel } from './NextStepsPanel'
import { LowCreditsCard } from './LowCreditsCard'
import { MemoryWarningBanner } from './MemoryWarningBanner'
import { Logo } from '@/components/logo'
import { Input } from '@/components/ui/input'
import type { Conversation, Message, GroupedTodos, Project, OptimisticAttachment, CreditPack, OrganizationBilling } from '@/types/conversations'

interface SelectedElementContext {
  selector?: string
  cayuSelector?: string
  tag?: string
  text?: string
  route?: string
  appType?: 'web' | 'mobile'
  source?: 'canvas' | 'preview'
  elements?: Array<{ id: string; type: string; text?: string }>
}

interface ChatPaneProps {
  conversation: Conversation
  project: Project
  messages: Message[]
  groupedTodos: GroupedTodos
  processing: boolean
  currentActivity: string | null
  activityHistory?: string[]
  isSendingMessage: boolean
  isAcceptingChanges?: boolean
  isDiscardingChanges?: boolean
  isInterrupting?: boolean
  onSendMessage: (content: string, signedBlobIds?: string[], selectedElementContext?: SelectedElementContext, optimisticAttachments?: OptimisticAttachment[]) => Promise<void>
  onToggleMode: () => void
  onSwitchToMode?: (mode: string) => Promise<void>
  onInterrupt?: () => Promise<void>
  onApprovePlan: (messageId: number) => Promise<void>
  onSubmitAnswers: (messageId: number, answers: Record<string, string | string[]>) => Promise<void>
  onConfirmButton: (messageId: number, prompt: string | undefined, clickedLabel?: string) => Promise<void>
  hasMoreMessages?: boolean
  isLoadingOlder?: boolean
  onLoadOlderMessages?: () => void
  nextStepSuggestions?: Array<{ name: string; prompt: string }>
  collapsedMessageIds?: Set<number>
  expandedTurns?: Set<number>
  onToggleTurnExpansion?: (userMessageId: number) => void
  // External content from iframe errors (ErrorBoundary "Send to Chat")
  externalPendingContent?: string
  onExternalPendingContentConsumed?: () => void
  // Element selection
  isSelectionModeActive?: boolean
  onToggleSelectionMode?: () => void
  selectedElement?: SelectedElementContext | null
  onClearSelectedElement?: () => void
  // Organization for asset picker
  organizationId?: number
  // Organization with full credit data for LowCreditsCard
  organization?: OrganizationBilling
  // Credit packs for purchasing
  creditPacks?: CreditPack[]
  // Undo feature - message ID that can be undone (null = no undo available)
  canUndoMessageId?: number | null
  // Rewinding state - disable input during rewind/undo
  isRewinding?: boolean
  onUndo?: () => void
  isBrowserUseTesting?: boolean
  activeWorkflow?: ActiveWorkflow | null
  buildingTask?: import('@/types/conversations').ProjectTask | undefined
  buildingTasks?: import('@/types/conversations').ProjectTask[]
  taskActivities?: Record<number, { current: string | null; history: string[] }>
  onStopTask?: (taskId: number) => Promise<any>
  requestChangesTask?: { id: number; title: string } | null
  onClearRequestChangesTask?: () => void
  memoryWarning?: {
    memory_percent: number
    current_size: string
    current_memory_gb: number
    suggested_size: string | null
    suggested_memory_gb: number | null
    instance_name: string
  } | null
  onDismissMemoryWarning?: () => void
  pmBrowserScreenshots?: Array<{ image: string; mediaType: string; caption: string; timestamp: number }>
}

export function ChatPane({
  conversation,
  project,
  messages,
  groupedTodos,
  processing,
  currentActivity,
  activityHistory = [],
  isSendingMessage,
  isAcceptingChanges = false,
  isDiscardingChanges = false,
  isInterrupting = false,
  onSendMessage,
  onToggleMode,
  onSwitchToMode,
  onInterrupt,
  onApprovePlan,
  onSubmitAnswers,
  onConfirmButton,
  hasMoreMessages,
  isLoadingOlder,
  onLoadOlderMessages,
  nextStepSuggestions = [],
  collapsedMessageIds,
  expandedTurns,
  onToggleTurnExpansion,
  externalPendingContent,
  onExternalPendingContentConsumed,
  isSelectionModeActive,
  onToggleSelectionMode,
  selectedElement,
  onClearSelectedElement,
  organizationId,
  organization,
  creditPacks = [],
  canUndoMessageId,
  isRewinding = false,
  onUndo,
  isBrowserUseTesting = false,
  activeWorkflow,
  buildingTask,
  buildingTasks = [],
  taskActivities = {},
  onStopTask,
  requestChangesTask,
  onClearRequestChangesTask,
  memoryWarning,
  onDismissMemoryWarning,
  pmBrowserScreenshots = [],
}: ChatPaneProps) {
  const { t } = useTranslation('conversations')
  const [pendingContent, setPendingContent] = useState<string | undefined>(undefined)
  const nextStepsPanelRef = useRef<HTMLDivElement>(null)
  const [panelHeight, setPanelHeight] = useState(0)

  useEffect(() => {
    if (!nextStepsPanelRef.current) { setPanelHeight(0); return }
    const observer = new ResizeObserver(([entry]) => {
      setPanelHeight(entry.contentRect.height)
    })
    observer.observe(nextStepsPanelRef.current)
    return () => observer.disconnect()
  }, [])

  const isWorkflowActive = checkWorkflowInProgress(activeWorkflow)
  const isWorkflowBlockingInput = isWorkflowActive && checkWorkflowBlocking(activeWorkflow)

  // Merge external content (from iframe errors) with internal pending content
  // External content takes priority when present
  const effectivePendingContent = externalPendingContent || pendingContent

  // Callback to clear both internal and external pending content
  const handlePendingContentConsumedMerged = useCallback(() => {
    setPendingContent(undefined)
    onExternalPendingContentConsumed?.()
  }, [onExternalPendingContentConsumed])

  const handleSuggestionClick = useCallback((prompt: string) => {
    setPendingContent(prompt)
  }, [])


  // Typewriter animation for project name when it changes from placeholder
  const [displayedName, setDisplayedName] = useState(project.name)
  const [isTyping, setIsTyping] = useState(false)
  const prevNameRef = useRef(project.name)

  useEffect(() => {
    const prevName = prevNameRef.current
    const newName = project.name

    // Only animate when changing FROM placeholder TO real name
    if (prevName === 'Untitled Project' && newName !== 'Untitled Project') {
      setIsTyping(true)
      setDisplayedName('')

      let index = 0
      const typeInterval = setInterval(() => {
        if (index < newName.length) {
          setDisplayedName(newName.slice(0, index + 1))
          index++
        } else {
          clearInterval(typeInterval)
          setIsTyping(false)
        }
      }, 40) // 40ms per character

      prevNameRef.current = newName
      return () => clearInterval(typeInterval)
    } else {
      // No animation for other changes
      setDisplayedName(newName)
      prevNameRef.current = newName
    }
  }, [project.name])

  // Inline editing for project name
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState(project.name)
  const nameInputRef = useRef<HTMLInputElement>(null)

  // Update editedName when project.name changes externally
  useEffect(() => {
    if (!isEditingName) {
      setEditedName(project.name)
    }
  }, [project.name, isEditingName])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [isEditingName])

  const handleNameSave = useCallback(async () => {
    const trimmedName = editedName.trim()
    if (!trimmedName || trimmedName === project.name) {
      setEditedName(project.name)
      setIsEditingName(false)
      return
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      const response = await fetch(`/projects/${project.id}/name`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({ name: trimmedName }),
      })

      if (response.ok) {
        // Optimistic update - instant feedback
        setDisplayedName(trimmedName)
        setEditedName(trimmedName)
        // Sync server state (page title, other components)
        router.reload()
      } else {
        setEditedName(project.name)
      }
    } catch {
      setEditedName(project.name)
    }
    setIsEditingName(false)
  }, [editedName, project.name, project.id])

  const handleNameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleNameSave()
    } else if (e.key === 'Escape') {
      setEditedName(project.name)
      setIsEditingName(false)
    }
  }, [handleNameSave, project.name])

  return (
    <div className="flex h-full flex-col">
      {/* Header — hidden on mobile (MobileConversationView has its own) */}
      <div className="hidden md:flex h-12 shrink-0 items-center gap-3 pl-2 pr-4 lg:pr-6 bg-content-bg">
        <Link
          href="/projects"
          className="group/logo flex items-center justify-center h-8 w-8 rounded-lg hover:bg-accent transition-colors"
        >
          <Logo iconOnly className="h-5 w-5 group-hover/logo:hidden" />
          <ArrowLeft className="h-5 w-5 hidden group-hover/logo:block text-foreground" />
        </Link>
        {isEditingName ? (
          <Input
            ref={nameInputRef}
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={handleNameKeyDown}
            className="font-semibold h-8 py-1 px-2 min-w-0 flex-1"
          />
        ) : (
          <div className="group flex items-center gap-1.5 min-w-0 flex-1">
            <h1 className="font-semibold text-foreground truncate">
              {displayedName}
              {isTyping && <span className="animate-pulse">|</span>}
            </h1>
            <button
              onClick={() => setIsEditingName(true)}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-accent rounded transition-all flex-shrink-0"
              title={t('chat.editName')}
            >
              <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </div>
        )}
      </div>

      {/* Messages Area — relative so NextStepsPanel can overlay without affecting scroll */}
      <div className="flex-1 overflow-hidden min-h-0 min-w-0 flex flex-col relative">
        <MessageList
          messages={messages}
          processing={processing}
          conversation={conversation}
          currentActivity={currentActivity}
          onApprovePlan={onApprovePlan}
          onSubmitAnswers={onSubmitAnswers}
          onConfirmButton={onConfirmButton}
          hasMoreMessages={hasMoreMessages}
          isLoadingOlder={isLoadingOlder}
          onLoadOlderMessages={onLoadOlderMessages}
          collapsedMessageIds={collapsedMessageIds}
          expandedTurns={expandedTurns}
          onToggleTurnExpansion={onToggleTurnExpansion}
          canUndoMessageId={canUndoMessageId}
          projectId={project.id}
          isRewinding={isRewinding}
          onUndo={onUndo}
          onSuggestionClick={handleSuggestionClick}
          activeWorkflow={activeWorkflow}
          onRetryMessage={async (errorMessageId) => {
            const response = await fetch(
              `/projects/${project.id}/features/${conversation.id}/messages/${errorMessageId}/retry`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
                }
              }
            )
            if (!response.ok) throw new Error('Retry failed')
          }}
          scrollButtonBottomOffset={panelHeight}
          onRequestInputFocus={() => {
            const textarea = document.querySelector<HTMLTextAreaElement>('[data-chat-input]')
            textarea?.focus()
          }}
          pmBrowserScreenshots={pmBrowserScreenshots}
        />

        {/* Bottom overlay stack — task badge above next steps, both above scroll */}
        <div ref={nextStepsPanelRef} className="absolute bottom-0 left-0 right-0 z-10">
          {/* Task building badges — one per active worker */}
          {onStopTask && buildingTasks.map(task => (
            <TaskBadge
              key={task.id}
              task={task}
              messages={messages}
              currentActivity={taskActivities[task.id]?.current ?? null}
              activityHistory={taskActivities[task.id]?.history ?? []}
              onStop={onStopTask}
            />
          ))}
          {/* Next Steps Panel */}
          {!isAcceptingChanges && !isDiscardingChanges && (
            <div>
              <NextStepsPanel
                conversation={conversation}
                messages={messages}
                onSuggestionClick={handleSuggestionClick}
                processing={processing}
                suggestions={nextStepSuggestions}
              />
            </div>
          )}
        </div>
      </div>

      {/* Activity Indicator - Rolling history (only in Build mode, not PM discussion) */}
      {activityHistory.length > 0 && conversation.mode === 'implementation' && (
        <div className="border-t rounded-t-xl bg-primary/5 dark:bg-primary/10 px-4 mx-2 -mb-2 pb-4 pt-2 flex-shrink-0 min-w-0 overflow-hidden">
          <div className="space-y-1.5">
            {/* Older activities (struck through) */}
            {activityHistory.slice(0, -1).map((activity, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground/50 font-mono line-through min-w-0">
                <div className="h-1.5 w-1.5 rounded-full bg-primary/30 flex-shrink-0" />
                <span className="truncate">{activity}</span>
              </div>
            ))}
            {/* Latest activity (prominent, pulsing) */}
            <div className="flex items-center gap-2 text-sm text-foreground/70 animate-pulse min-w-0">
              <div className="h-2 w-2 rounded-full bg-primary animate-ping flex-shrink-0" />
              <span className="font-medium truncate">{activityHistory[activityHistory.length - 1]}</span>
            </div>
          </div>
        </div>
      )}

      {/* Git Operation Indicator */}
      {(isAcceptingChanges || isDiscardingChanges) && (
        <div className="rounded-xl border bg-content-card p-4 mb-2 mx-2 flex-shrink-0">
          <div className="flex items-center gap-3 text-sm">
            <div className="h-3 w-3 rounded-full bg-amber-500 animate-ping" />
            <div>
              <div className="font-semibold text-amber-700 dark:text-amber-400">
                {isAcceptingChanges ? t('chat.saving', { title: conversation.title }) : t('chat.discarding', { title: conversation.title })}
              </div>
              <div className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
                {t('chat.pleaseWait')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Next Steps Panel moved inside Messages Area as absolute overlay */}

      {/* Memory Warning Banner */}
      {!isAcceptingChanges && !isDiscardingChanges && memoryWarning && onDismissMemoryWarning && (
        <MemoryWarningBanner
          projectId={project.id}
          warning={memoryWarning}
          isFreePlan={organization?.current_plan === 'free'}
          onDismiss={onDismissMemoryWarning}
        />
      )}

      {/* Low Credits Warning Card */}
      {!isAcceptingChanges && !isDiscardingChanges && organization && (
        <LowCreditsCard
          organization={organization}
          creditPacks={creditPacks}
        />
      )}

      {/* Message Input - Hidden during git operations */}
      {!isAcceptingChanges && !isDiscardingChanges && (
        <div className="flex-shrink-0">
          <MessageInput
            conversation={conversation}
            onSendMessage={onSendMessage}
            onToggleMode={onToggleMode}
            onSwitchToMode={onSwitchToMode}
            onInterrupt={onInterrupt}
            disabled={!conversation.can_accept_messages || (processing && !isWorkflowActive) || isRewinding || isWorkflowBlockingInput || project.import_status === 'importing' || project.instance_status !== 'active'}
            isSending={isSendingMessage}
            isProcessing={processing}
            isInterrupting={isInterrupting}
            isWorkflowInProgress={isWorkflowActive}
            pendingContent={effectivePendingContent}
            onPendingContentConsumed={handlePendingContentConsumedMerged}
            productSpec={project.product_spec?.content}
            isSelectionModeActive={isSelectionModeActive}
            onToggleSelectionMode={onToggleSelectionMode}
            selectedElement={selectedElement}
            onClearSelectedElement={onClearSelectedElement}
            organizationId={organizationId}
            projectId={project.id}
            isBrowserUseTesting={isBrowserUseTesting}
            showModeSelector={project.project_type !== 'managed_agent'}
            requestChangesTask={requestChangesTask}
            onClearRequestChangesTask={onClearRequestChangesTask}
          />
        </div>
      )}
    </div>
  )
}
